import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, FULL_MENU, MARK, LOGO } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";
import { useLang } from "../../i18n";

export const Nav = () => {
  const { c, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[var(--bg-primary)]/90 backdrop-blur-xl border-b border-[var(--border)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 h-24 flex items-center justify-between gap-4">
          <button data-testid="nav-logo" onClick={() => go("#top")} className="shrink-0 flex items-center gap-3">
            <img src={MARK} alt="" className="h-12 md:h-14 w-auto object-contain" />
            <span className="flex flex-col leading-none text-left">
              <span className="font-serif text-lg md:text-2xl tracking-tight text-[var(--text-primary)]">Ancion</span>
              <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mt-1">Consulting</span>
            </span>
          </button>

          <nav className="hidden md:flex flex-1 items-center justify-center gap-8">
            {c.nav.links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                onClick={() => go(l.href)}
                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors link-underline"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta"
              onClick={() => go("#contact")}
              className="px-6 py-2.5 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold text-sm hover:bg-[var(--accent-hover)] transition-colors duration-300"
            >
              {c.nav.cta}
            </button>
          </nav>

          <div className="shrink-0 flex items-center gap-5">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              aria-label="Switch language"
              className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <span className={lang === "en" ? "text-[var(--accent)]" : ""}>EN</span>
              <span className="mx-1 opacity-40">/</span>
              <span className={lang === "fr" ? "text-[var(--accent)]" : ""}>FR</span>
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="text-[var(--text-primary)] hover:text-[var(--accent)] transition-colors"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="full-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-[var(--bg-primary)] flex flex-col p-8 md:p-16"
          >
            <div className="flex justify-between items-center">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
                <img src={LOGO} alt="Ancion Consulting" className="h-14 md:h-16 w-auto object-contain" />
              </span>
              <button data-testid="menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="text-[var(--text-primary)] hover:text-[var(--accent)] transition-colors">
                <X size={30} />
              </button>
            </div>

            <nav className="flex-1 flex flex-col justify-center gap-4 md:gap-5 max-w-3xl mx-auto w-full">
              {c.nav.fullMenu.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`menu-link-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-baseline gap-5 text-left"
                >
                  <span className="font-mono text-xs text-[var(--accent)] w-8">0{i + 1}</span>
                  <span className="font-serif text-3xl md:text-5xl tracking-tight text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors duration-300">
                    {l.label}
                  </span>
                </motion.button>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + c.nav.fullMenu.length * 0.07 }}
              className="max-w-3xl mx-auto w-full"
            >
              <button
                data-testid="menu-cta"
                onClick={() => go("#contact")}
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--accent-hover)] transition-colors duration-300"
              >
                {c.nav.cta}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
